import React, { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  apiService,
  BackfillExecuteResult,
  BackfillPreviewResult,
  BackofficeSyncPreviewResult,
  BackofficeSyncExecuteResult,
  DealerGoalBackfillResult,
  MissingRecordPreviewItem,
  MissingRecordsPreviewResult,
} from "../services/api";
import { useAuth } from "../contexts/AuthContext";

type Phase = "upload" | "previewing" | "preview" | "executing" | "done";
type Tab = "customers" | "dealers";
type SyncPhase = "idle" | "loading" | "preview" | "syncing" | "done";

const BackofficeBackfill: React.FC = () => {
  useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Top-level tab
  const [mainTab, setMainTab] = useState<"step1" | "step2" | "step3" | "step4">(
    "step1",
  );

  // Step 4 state (Dealer Goal Email Backfill)
  const [goalPhase, setGoalPhase] = useState<
    "idle" | "loading" | "executing" | "done"
  >("idle");
  const [goalPreview, setGoalPreview] =
    useState<DealerGoalBackfillResult | null>(null);
  const [goalResult, setGoalResult] = useState<DealerGoalBackfillResult | null>(
    null,
  );

  // Step 1 state
  const [phase, setPhase] = useState<Phase>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BackfillPreviewResult | null>(null);
  const [result, setResult] = useState<BackfillExecuteResult | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("customers");
  const [isDragOver, setIsDragOver] = useState(false);

  // Step 2 state
  const syncFileInputRef = useRef<HTMLInputElement>(null);
  const [syncPhase, setSyncPhase] = useState<SyncPhase>("idle");
  const [syncFile, setSyncFile] = useState<File | null>(null);
  const [syncIsDragOver, setSyncIsDragOver] = useState(false);
  const [syncPreview, setSyncPreview] =
    useState<BackofficeSyncPreviewResult | null>(null);
  const [syncResult, setSyncResult] =
    useState<BackofficeSyncExecuteResult | null>(null);
  const [syncTab, setSyncTab] = useState<Tab>("customers");
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [updatedIds, setUpdatedIds] = useState<Set<string>>(new Set());

  // Step 3 state (Missing Records Recovery)
  const recoveryFileInputRef = useRef<HTMLInputElement>(null);
  type RecoveryPhase = "idle" | "loading" | "preview";
  const [recoveryPhase, setRecoveryPhase] = useState<RecoveryPhase>("idle");
  const [recoveryFile, setRecoveryFile] = useState<File | null>(null);
  const [recoveryIsDragOver, setRecoveryIsDragOver] = useState(false);
  const [recoveryPreview, setRecoveryPreview] =
    useState<MissingRecordsPreviewResult | null>(null);
  const [creatingIds, setCreatingIds] = useState<Set<string>>(new Set());
  const [createdIds, setCreatedIds] = useState<Set<string>>(new Set());

  // Step 1 per-row UUID set state
  const [uuidSettingIds, setUuidSettingIds] = useState<Set<string>>(new Set());
  const [uuidSetIds, setUuidSetIds] = useState<Set<string>>(new Set());

  // Step 1 handlers
  const handleFile = (f: File) => {
    if (!f.name.endsWith(".csv")) {
      toast.error("Please select a .csv file.");
      return;
    }
    setFile(f);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, []);

  const handlePreview = async () => {
    if (!file) return;
    setPhase("previewing");
    try {
      const data = await apiService.backfillPreview(file);
      setPreview(data);
      setActiveTab(data.customers.length > 0 ? "customers" : "dealers");
      setPhase("preview");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Preview failed.");
      setPhase("upload");
    }
  };

  const handleExecute = async () => {
    if (!file || !preview) return;
    const confirmed = window.confirm(
      `${preview.summary.customerToUpdate} customers and ${preview.summary.dealerToUpdate} dealers will be updated.\n\nDo you want to proceed?`,
    );
    if (!confirmed) return;
    setPhase("executing");
    try {
      const data = await apiService.backfillExecute(file);
      setResult(data);
      setPhase("done");
      toast.success("Backfill completed successfully!");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Execute failed.");
      setPhase("preview");
    }
  };

  const handleSetUuidOne = async (
    type: "customer" | "dealer",
    id: string,
    backofficeUUID: string,
  ) => {
    setUuidSettingIds((prev) => new Set(prev).add(id));
    try {
      await apiService.backfillUuidSetOne(type, id, backofficeUUID);
      setUuidSetIds((prev) => new Set(prev).add(id));
      toast.success(`UUID set for ${id}`);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || `Failed to set UUID for ${id}`,
      );
    } finally {
      setUuidSettingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  };

  const reset = () => {
    setPhase("upload");
    setFile(null);
    setPreview(null);
    setResult(null);
    setUuidSettingIds(new Set());
    setUuidSetIds(new Set());
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Step 2 handlers
  const handleSyncFile = (f: File) => {
    if (!f.name.endsWith(".csv")) {
      toast.error("Please select a .csv file.");
      return;
    }
    setSyncFile(f);
  };

  const handleSyncDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setSyncIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleSyncFile(f);
  };

  const handleSyncPreview = async () => {
    if (!syncFile) return;
    setSyncPhase("loading");
    try {
      const data = await apiService.backofficeSyncPreview(syncFile);
      setSyncPreview(data);
      setSyncTab(data.customers.length > 0 ? "customers" : "dealers");
      setSyncPhase("preview");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Sync preview failed.");
      setSyncPhase("idle");
    }
  };

  const handleSyncExecute = async () => {
    if (!syncPreview) return;
    const total =
      syncPreview.summary.customerCount + syncPreview.summary.dealerCount;
    const confirmed = window.confirm(
      `${total} records will be pushed to backoffice.\n\nThis calls the backoffice PATCH endpoint for each record. Continue?`,
    );
    if (!confirmed) return;
    setSyncPhase("syncing");
    try {
      const data = await apiService.backofficeSyncExecute();
      setSyncResult(data);
      setSyncPhase("done");
      toast.success("Backoffice sync started in background!");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Sync failed.");
      setSyncPhase("preview");
    }
  };

  const resetSync = () => {
    setSyncPhase("idle");
    setSyncPreview(null);
    setSyncResult(null);
    setSyncFile(null);
    setUpdatingIds(new Set());
    setUpdatedIds(new Set());
    if (syncFileInputRef.current) syncFileInputRef.current.value = "";
  };

  // Step 4 handlers (Dealer Goal Email Backfill)
  const handleGoalPreview = async () => {
    setGoalPhase("loading");
    try {
      const data = await apiService.dealerGoalEmailPreview();
      setGoalPreview(data);
      setGoalResult(null);
      setGoalPhase("idle");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Preview failed.");
      setGoalPhase("idle");
    }
  };

  const handleGoalExecute = async () => {
    if (!goalPreview) return;
    const confirmed = window.confirm(
      `${goalPreview.qualified.length} dealers will be marked as "goal email sent" WITHOUT sending any email, so they never receive a late "goal reached" notification.\n\nContinue?`,
    );
    if (!confirmed) return;
    setGoalPhase("executing");
    try {
      const data = await apiService.dealerGoalEmailExecute();
      setGoalResult(data);
      setGoalPhase("done");
      toast.success("Dealer goal email backfill completed!");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Execute failed.");
      setGoalPhase("idle");
    }
  };

  const handleUpdateOne = async (type: "customer" | "dealer", id: string) => {
    setUpdatingIds((prev) => new Set(prev).add(id));
    try {
      await apiService.backofficeSyncExecuteOne(type, id);
      setUpdatedIds((prev) => new Set(prev).add(id));
      toast.success(`${id} updated`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to update ${id}`);
    } finally {
      setUpdatingIds((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  };

  // Step 1 content
  const renderStep1 = () => {
    if (phase === "previewing" || phase === "executing") {
      const isExec = phase === "executing";
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div
            className={`h-10 w-10 border-4 ${
              isExec ? "border-green-500" : "border-blue-500"
            } border-t-transparent rounded-full animate-spin`}
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isExec
              ? "Executing backfill, please wait…"
              : "Running preview, please wait…"}
          </p>
        </div>
      );
    }

    if (phase === "upload") {
      return (
        <div className="space-y-6 max-w-2xl">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Upload the WBO CSV export — preview first, then execute when ready.
          </p>
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragOver
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/30"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <ArrowUpTrayIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            {file ? (
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(file.size / 1024).toFixed(1)} KB — click again to change
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Drag & drop your CSV file or click to browse
                </p>
                <p className="text-xs text-gray-400 mt-1">Max 20 MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleFile(e.target.files[0]);
              }}
            />
          </div>
          <button
            onClick={handlePreview}
            disabled={!file}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <DocumentMagnifyingGlassIcon className="h-5 w-5" />
            Preview (Dry-Run)
          </button>
        </div>
      );
    }

    if (phase === "preview" && preview) {
      const { summary } = preview;
      const totalToUpdate = summary.customerToUpdate + summary.dealerToUpdate;
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {file?.name}
            </p>
            <button
              onClick={reset}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
            >
              Choose a different file
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="To update" value={totalToUpdate} color="blue" />
            <SummaryCard
              label="Already set"
              value={summary.customerSkipped + summary.dealerSkipped}
              color="gray"
            />
            <SummaryCard
              label="Not found"
              value={summary.customerNotFound + summary.dealerNotFound}
              color="yellow"
            />
            <SummaryCard
              label="CSV rows"
              value={summary.totalParsedRows}
              color="gray"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Customers
              </p>
              <p className="text-3xl font-bold text-blue-600">
                {summary.customerToUpdate}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {summary.customerSkipped} skipped · {summary.customerNotFound}{" "}
                not found
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Dealers
              </p>
              <p className="text-3xl font-bold text-indigo-600">
                {summary.dealerToUpdate}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {summary.dealerSkipped} skipped · {summary.dealerNotFound} not
                found
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              {(["customers", "dealers"] as Tab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                      : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  {tab === "customers"
                    ? `Customers (${preview.customers.length})`
                    : `Dealers (${preview.dealers.length})`}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-40">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                      Field
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Before
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      After
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(activeTab === "customers"
                    ? preview.customers
                    : preview.dealers
                  ).map((row, i) => {
                    const allRows = [
                      {
                        label: "Backoffice UUID",
                        oldValue: row.idSalesBackoffice ?? "",
                        newValue: row.backofficeUUID_csv,
                        willUpdate: true,
                      },
                      ...row.fieldChanges.map((fc) => ({
                        label: fc.label,
                        oldValue: fc.oldValue,
                        newValue: fc.newValue,
                        willUpdate: fc.willUpdate,
                      })),
                    ];
                    return (
                      <React.Fragment key={i}>
                        {allRows.map((r, j) => (
                          <tr
                            key={`${i}-${j}`}
                            className={`${
                              j === 0
                                ? "border-t-2 border-gray-200 dark:border-gray-600"
                                : ""
                            } hover:bg-gray-50 dark:hover:bg-gray-700/40`}
                          >
                            {j === 0 ? (
                              <td
                                rowSpan={allRows.length}
                                className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300 align-top pt-3 border-r border-gray-100 dark:border-gray-700"
                              >
                                <div className="flex flex-col gap-2">
                                  <span>{row.id}</span>
                                  {uuidSetIds.has(row.id) ? (
                                    <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
                                      <CheckCircleIcon className="h-3.5 w-3.5" />{" "}
                                      UUID Set
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        handleSetUuidOne(
                                          row.type,
                                          row.id,
                                          row.backofficeUUID_csv,
                                        )
                                      }
                                      disabled={uuidSettingIds.has(row.id)}
                                      className="inline-flex items-center justify-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      {uuidSettingIds.has(row.id) ? (
                                        <>
                                          <ArrowPathIcon className="h-3 w-3 animate-spin" />{" "}
                                          Setting…
                                        </>
                                      ) : (
                                        <>
                                          <ArrowPathIcon className="h-3 w-3" />{" "}
                                          Set UUID
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>
                            ) : null}
                            <td className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              {r.label}
                            </td>
                            <td className="px-4 py-1.5 font-mono text-xs">
                              {!r.oldValue ? (
                                <span className="text-gray-400 italic">
                                  empty
                                </span>
                              ) : r.willUpdate ? (
                                <span className="line-through text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">
                                  {r.oldValue}
                                </span>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400 px-1.5 py-0.5">
                                  {r.oldValue}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-center text-gray-400 text-xs">
                              {r.willUpdate ? "→" : "="}
                            </td>
                            <td className="px-4 py-1.5 font-mono text-xs">
                              {r.willUpdate ? (
                                <span
                                  className={`${
                                    !r.oldValue
                                      ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20"
                                      : "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20"
                                  } px-1.5 py-0.5 rounded font-semibold`}
                                >
                                  {r.newValue}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 px-1.5 py-0.5">
                                  {r.newValue || <em>empty</em>}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {(activeTab === "customers"
                    ? preview.customers
                    : preview.dealers
                  ).length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-400 text-sm"
                      >
                        No records to update in this category
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalToUpdate > 0 ? (
            <button
              onClick={handleExecute}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              <CheckCircleIcon className="h-5 w-5" />
              Execute — update {totalToUpdate} records
            </button>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              Nothing to update — all records already have a backoffice UUID
              set.
            </div>
          )}
        </div>
      );
    }

    if (phase === "done" && result) {
      const { summary } = result;
      return (
        <div className="space-y-6 max-w-2xl">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Done
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Backfill applied successfully.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Customers
              </p>
              <dl className="space-y-1 text-sm">
                <Row label="Updated" value={summary.customerUpdated} green />
                <Row
                  label="Already set (skipped)"
                  value={summary.customerSkipped}
                />
                <Row label="Not found" value={summary.customerNotFound} />
              </dl>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Dealers
              </p>
              <dl className="space-y-1 text-sm">
                <Row label="Updated" value={summary.dealerUpdated} green />
                <Row
                  label="Already set (skipped)"
                  value={summary.dealerSkipped}
                />
                <Row label="Not found" value={summary.dealerNotFound} />
              </dl>
            </div>
          </div>
          <button
            onClick={reset}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            New operation
          </button>
        </div>
      );
    }

    return null;
  };

  // Step 2 content
  const renderStep2 = () => {
    if (syncPhase === "loading" || syncPhase === "syncing") {
      const isSyncing = syncPhase === "syncing";
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div
            className={`h-10 w-10 border-4 ${
              isSyncing ? "border-green-500" : "border-purple-500"
            } border-t-transparent rounded-full animate-spin`}
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isSyncing
              ? "Pushing data to backoffice, please wait…"
              : "Loading records, please wait…"}
          </p>
        </div>
      );
    }

    if (syncPhase === "idle") {
      return (
        <div className="space-y-6 max-w-2xl">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">What this does</p>
            <p>
              Upload the same WBO CSV export. The preview will compare{" "}
              <strong>CSV values</strong> (current backoffice state) vs{" "}
              <strong>BPE DB values</strong> (what will be pushed) — so you can
              see exactly what changes.
            </p>
          </div>
          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
              syncIsDragOver
                ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                : "border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:bg-gray-50 dark:hover:bg-gray-700/30"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setSyncIsDragOver(true);
            }}
            onDragLeave={() => setSyncIsDragOver(false)}
            onDrop={handleSyncDrop}
            onClick={() => syncFileInputRef.current?.click()}
          >
            <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            {syncFile ? (
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white">
                  {syncFile.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(syncFile.size / 1024).toFixed(1)} KB — click again to change
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Drag & drop WBO CSV or click to browse
              </p>
            )}
            <input
              ref={syncFileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleSyncFile(e.target.files[0]);
              }}
            />
          </div>
          <button
            onClick={handleSyncPreview}
            disabled={!syncFile}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <DocumentMagnifyingGlassIcon className="h-5 w-5" />
            Preview — Compare CSV vs BPE DB
          </button>
        </div>
      );
    }

    if (syncPhase === "preview" && syncPreview) {
      const total =
        syncPreview.summary.customerCount + syncPreview.summary.dealerCount;
      const items =
        syncTab === "customers" ? syncPreview.customers : syncPreview.dealers;
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-5 py-3 text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {syncPreview.summary.customerCount}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Customers</p>
                <p className="text-xs text-purple-500 mt-0.5">
                  {syncPreview.summary.customerFieldChanges} field changes
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-5 py-3 text-center">
                <p className="text-2xl font-bold text-indigo-600">
                  {syncPreview.summary.dealerCount}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Dealers</p>
                <p className="text-xs text-indigo-500 mt-0.5">
                  {syncPreview.summary.dealerFieldChanges} field changes
                </p>
              </div>
            </div>
            <button
              onClick={resetSync}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
            >
              Reset
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              {(["customers", "dealers"] as Tab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSyncTab(tab)}
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    syncTab === tab
                      ? "border-b-2 border-purple-500 text-purple-600 dark:text-purple-400"
                      : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  {tab === "customers"
                    ? `Customers (${syncPreview.customers.length})`
                    : `Dealers (${syncPreview.dealers.length})`}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto max-h-[550px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-36">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20">
                      Field
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Backoffice (CSV)
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      BPE DB (push)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <React.Fragment key={i}>
                      {item.fieldDiffs.map((f, j) => (
                        <tr
                          key={`${i}-${j}`}
                          className={`${
                            j === 0
                              ? "border-t-2 border-gray-200 dark:border-gray-600"
                              : ""
                          } hover:bg-gray-50 dark:hover:bg-gray-700/40`}
                        >
                          {j === 0 ? (
                            <td
                              rowSpan={item.fieldDiffs.length}
                              className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300 align-top pt-3 border-r border-gray-100 dark:border-gray-700"
                            >
                              <div className="flex flex-col gap-2">
                                <span>{item.id}</span>
                                {updatedIds.has(item.id) ? (
                                  <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
                                    <CheckCircleIcon className="h-3.5 w-3.5" />{" "}
                                    Updated
                                  </span>
                                ) : (
                                  <button
                                    onClick={() =>
                                      handleUpdateOne(item.type, item.id)
                                    }
                                    disabled={updatingIds.has(item.id)}
                                    className="inline-flex items-center justify-center gap-1 px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    {updatingIds.has(item.id) ? (
                                      <>
                                        <ArrowPathIcon className="h-3 w-3 animate-spin" />{" "}
                                        Updating…
                                      </>
                                    ) : (
                                      <>
                                        <ArrowPathIcon className="h-3 w-3" />{" "}
                                        Update
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                          ) : null}
                          <td className="px-4 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {f.label}
                          </td>
                          <td className="px-4 py-1.5 font-mono text-xs">
                            {!f.csvValue ? (
                              <span className="text-gray-400 italic">
                                empty
                              </span>
                            ) : f.willChange ? (
                              <span className="line-through text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">
                                {f.csvValue}
                              </span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400 px-1.5 py-0.5">
                                {f.csvValue}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center text-gray-400 text-xs">
                            {f.willChange ? "→" : "="}
                          </td>
                          <td className="px-4 py-1.5 font-mono text-xs">
                            {f.willChange ? (
                              <span className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded font-semibold">
                                {f.bpeValue}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 px-1.5 py-0.5">
                                {f.bpeValue || <em>empty</em>}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-gray-400 text-sm"
                      >
                        No records in this category
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {total > 0 ? (
            <button
              onClick={handleSyncExecute}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Sync {total} records to backoffice
            </button>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              No records with backoffice UUID found.
            </div>
          )}
        </div>
      );
    }

    if (syncPhase === "done" && syncResult) {
      return (
        <div className="space-y-6 max-w-2xl">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Sync Started
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {syncResult.message}
              </p>
            </div>
          </div>
          <button
            onClick={resetSync}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            New sync
          </button>
        </div>
      );
    }

    return null;
  };

  // Step 3: Missing Records Recovery

  const handleRecoveryFile = (f: File) => {
    if (!f.name.endsWith(".csv")) {
      toast.error("Please select a .csv file.");
      return;
    }
    setRecoveryFile(f);
  };

  const handleRecoveryDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setRecoveryIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleRecoveryFile(f);
  }, []);

  const handleRecoveryPreview = async () => {
    if (!recoveryFile) return;
    setRecoveryPhase("loading");
    try {
      const data = await apiService.missingRecordsPreview(recoveryFile);
      setRecoveryPreview(data);
      setRecoveryPhase("preview");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Preview failed.");
      setRecoveryPhase("idle");
    }
  };

  const handleCreateOne = async (item: MissingRecordPreviewItem) => {
    setCreatingIds((prev) => new Set(prev).add(item.csvId));
    try {
      await apiService.missingRecordsCreateOne({
        type: item.type,
        id: item.csvId,
        email: item.csvEmail || undefined,
        firstName: item.csvRow.firstName || undefined,
        lastName: item.csvRow.lastName || undefined,
        companyName: item.csvRow.companyName || undefined,
        phone: item.csvRow.phone || undefined,
        street: item.csvRow.street || undefined,
        zipCode: item.csvRow.zipCode || undefined,
        city: item.csvRow.city || undefined,
        country: item.csvRow.country || undefined,
        sponsorId: item.csvRow.sponsorId || undefined,
        backofficeUUID: item.csvBackofficeUUID || undefined,
      });
      setCreatedIds((prev) => new Set(prev).add(item.csvId));
      toast.success(`${item.csvId} created successfully`);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || `Failed to create ${item.csvId}`,
      );
    } finally {
      setCreatingIds((prev) => {
        const s = new Set(prev);
        s.delete(item.csvId);
        return s;
      });
    }
  };

  const renderStep3 = () => {
    // Upload phase
    if (recoveryPhase === "idle") {
      return (
        <div className="space-y-6 max-w-2xl">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
              Missing Records Recovery
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
              Upload a CSV with records that exist in the backoffice but are
              missing in ePoint. Preview first to see which records will be
              created. Users will need to reset their password via the
              forgot-password flow.
            </p>
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 font-mono">
              CSV format:
              type;email;id;backofficeUUID;firstName;lastName;companyName;country;sponsorId
            </p>
          </div>
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              recoveryIsDragOver
                ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                : "border-gray-300 dark:border-gray-600 hover:border-red-400 hover:bg-gray-50 dark:hover:bg-gray-700/30"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setRecoveryIsDragOver(true);
            }}
            onDragLeave={() => setRecoveryIsDragOver(false)}
            onDrop={handleRecoveryDrop}
            onClick={() => recoveryFileInputRef.current?.click()}
          >
            <ArrowUpTrayIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            {recoveryFile ? (
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white">
                  {recoveryFile.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(recoveryFile.size / 1024).toFixed(1)} KB — click again to
                  change
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Drag & drop your CSV file or click to browse
                </p>
                <p className="text-xs text-gray-400 mt-1">Max 20 MB</p>
              </div>
            )}
            <input
              ref={recoveryFileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) handleRecoveryFile(e.target.files[0]);
              }}
            />
          </div>
          <button
            onClick={handleRecoveryPreview}
            disabled={!recoveryFile}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <DocumentMagnifyingGlassIcon className="h-5 w-5" />
            Preview (Dry-Run)
          </button>
        </div>
      );
    }

    // Loading
    if (recoveryPhase === "loading") {
      return (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-red-500" />
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            Comparing CSV against ePoint database...
          </span>
        </div>
      );
    }

    // Preview
    if (recoveryPhase === "preview" && recoveryPreview) {
      const { summary, missing, existing } = recoveryPreview;
      return (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4">
            <SummaryCard
              label="Total CSV Rows"
              value={summary.totalCsvRows}
              color="gray"
            />
            <SummaryCard
              label="Missing in ePoint"
              value={summary.missingInEpoint}
              color="blue"
            />
            <SummaryCard
              label="Already Exist"
              value={summary.alreadyExist}
              color="indigo"
            />
            <SummaryCard
              label="Parse Errors"
              value={summary.parseErrors}
              color="yellow"
            />
          </div>

          {/* Missing records table */}
          {missing.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">
                Missing in ePoint ({missing.length}) — create individually
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[550px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Country
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Sponsor
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {missing.map((item, i) => (
                      <tr
                        key={i}
                        className={
                          createdIds.has(item.csvId)
                            ? "bg-green-50/50 dark:bg-green-900/10"
                            : "bg-red-50/50 dark:bg-red-900/10"
                        }
                      >
                        <td className="px-3 py-2">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                              item.type === "customer"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                            }`}
                          >
                            {item.type}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                          {item.csvId}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 max-w-[180px] truncate">
                          {item.csvEmail || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                          {item.csvName || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                          {item.csvRow?.country || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                          {item.csvRow?.sponsorId || "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {createdIds.has(item.csvId) ? (
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
                              <CheckCircleIcon className="h-3.5 w-3.5" />{" "}
                              Created
                            </span>
                          ) : (
                            <button
                              onClick={() => handleCreateOne(item)}
                              disabled={creatingIds.has(item.csvId)}
                              className="inline-flex items-center justify-center gap-1 px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {creatingIds.has(item.csvId) ? (
                                <>
                                  <ArrowPathIcon className="h-3 w-3 animate-spin" />{" "}
                                  Creating…
                                </>
                              ) : (
                                "Create"
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Existing records table */}
          {existing.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-2">
                Already in ePoint ({existing.length}) — will be skipped
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-500">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-gray-500">
                        CSV ID
                      </th>
                      <th className="px-4 py-2 text-left text-gray-500">
                        DB ID
                      </th>
                      <th className="px-4 py-2 text-left text-gray-500">
                        Email
                      </th>
                      <th className="px-4 py-2 text-left text-gray-500">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {existing.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                              item.type === "customer"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                            }`}
                          >
                            {item.type}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                          {item.csvId}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                          {item.dbRecord?.id || "—"}
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                          {item.dbRecord?.email || item.csvEmail}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                              item.dbRecord?.backofficeStatus === "success"
                                ? "bg-green-100 text-green-700"
                                : item.dbRecord?.backofficeStatus === "failed"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {item.dbRecord?.backofficeStatus || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Progress & actions */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {createdIds.size > 0 && (
                <span className="text-green-600 dark:text-green-400 font-medium">
                  {createdIds.size} / {missing.length} created
                </span>
              )}
            </div>
            <button
              onClick={() => {
                setRecoveryPhase("idle");
                setRecoveryPreview(null);
                setRecoveryFile(null);
                setCreatingIds(new Set());
                setCreatedIds(new Set());
              }}
              className="px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  // Step 4 content (Dealer Goal Email Backfill)
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Non-AI Dealer Goal Email Backfill
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Marks non-AI dealers that ALREADY satisfied the 1 transaction + 1
          sponsored customer goal as "email sent" — without sending any email.
          Run this so long-qualified dealers don't receive a false "goal
          reached" notification on their next transaction. Preview is read-only.
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleGoalPreview}
            disabled={goalPhase === "loading" || goalPhase === "executing"}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <DocumentMagnifyingGlassIcon className="w-4 h-4" />
            {goalPhase === "loading" ? "Checking..." : "Preview (dry-run)"}
          </button>
          <button
            onClick={handleGoalExecute}
            disabled={
              !goalPreview ||
              goalPreview.qualified.length === 0 ||
              goalPhase === "executing" ||
              goalPhase === "done"
            }
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            <CheckCircleIcon className="w-4 h-4" />
            {goalPhase === "executing" ? "Executing..." : "Execute"}
          </button>
        </div>

        {goalPreview && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <SummaryCard
                label="Dealers checked"
                value={goalPreview.checked}
                color="blue"
              />
              <SummaryCard
                label="Already qualified (will be marked)"
                value={goalPreview.qualified.length}
                color="yellow"
              />
            </div>
            {goalPreview.qualified.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">
                  Dealer IDs that would be marked:
                </p>
                <ul className="text-xs font-mono text-gray-700 dark:text-gray-300 space-y-0.5">
                  {goalPreview.qualified.map((id) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {goalResult && (
          <p className="text-sm font-medium text-green-600 dark:text-green-400">
            Done — {goalResult.qualified.length} dealers marked (out of{" "}
            {goalResult.checked} checked). They will not receive a late goal
            email.
          </p>
        )}
      </div>
    </div>
  );

  // Main render
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Backoffice Backfill
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Step 1: fill in missing backoffice UUIDs from CSV export. Step 2: push
          BPE data to backoffice.
        </p>
      </div>

      {/* Main tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setMainTab("step1")}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${
            mainTab === "step1"
              ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
              : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Step 1 — UUID Backfill
        </button>
        <button
          onClick={() => setMainTab("step2")}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${
            mainTab === "step2"
              ? "border-b-2 border-purple-500 text-purple-600 dark:text-purple-400"
              : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Step 2 — Sync to Backoffice
        </button>
        <button
          onClick={() => setMainTab("step3")}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${
            mainTab === "step3"
              ? "border-b-2 border-red-500 text-red-600 dark:text-red-400"
              : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Step 3 — Missing Records Recovery
        </button>
        <button
          onClick={() => setMainTab("step4")}
          className={`px-5 py-2.5 text-sm font-medium transition-colors ${
            mainTab === "step4"
              ? "border-b-2 border-green-500 text-green-600 dark:text-green-400"
              : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Step 4 — Dealer Goal Email
        </button>
      </div>

      <div>
        {mainTab === "step1" && renderStep1()}
        {mainTab === "step2" && renderStep2()}
        {mainTab === "step3" && renderStep3()}
        {mainTab === "step4" && renderStep4()}
      </div>
    </div>
  );
};

// Small Helpers

const SummaryCard: React.FC<{
  label: string;
  value: number;
  color: string;
}> = ({ label, value, color }) => {
  const colorMap: Record<string, string> = {
    blue: "text-blue-600 dark:text-blue-400",
    indigo: "text-indigo-600 dark:text-indigo-400",
    yellow: "text-yellow-600 dark:text-yellow-400",
    gray: "text-gray-600 dark:text-gray-400",
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
      <p className={`text-2xl font-bold ${colorMap[color] ?? colorMap.gray}`}>
        {value}
      </p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
};

const Row: React.FC<{ label: string; value: number; green?: boolean }> = ({
  label,
  value,
  green,
}) => (
  <div className="flex justify-between">
    <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
    <dd
      className={`font-semibold ${
        green
          ? "text-green-600 dark:text-green-400"
          : "text-gray-800 dark:text-gray-200"
      }`}
    >
      {value}
    </dd>
  </div>
);

export default BackofficeBackfill;
